/*---------------------------------------------------------------
 Project: Ring2All Platform
 Author: Rodrigo Cuadra
 Company: Ring2All LLC
 File: ClassOfServicesPage.tsx
 Created: 2025-01-27
 Description:
    Main page for Class of Services management with list view.

 Notes:
    - Refactored from monolithic file
    - Uses hooks for state management
    - Delegates data fetching to useClassOfServiceList

 Last Updated: 2025-01-27 by Rodrigo Cuadra (Refactored)
----------------------------------------------------------------*/

import { useState, useCallback, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Shield, Edit, Copy, Trash2 } from 'lucide-react';
import DataGridSimple from '../../components/ui/DataGridSimple';
import ConfirmationModal from '../../components/ui/ConfirmationModal';
import SimpleTooltip from '../../components/ui/SimpleTooltip';
import { ModulePageContainer } from '../../components/layouts';
import { useI18n } from '../../hooks/useI18n';
import { useNotifications } from '../../hooks/useNotifications';
import { useDataGrid } from '../../hooks/useDataGrid';
import { useContextStore } from '../../store/context';
import { useClassOfServiceList } from './hooks/useClassOfServiceList';
import {
  deleteClassOfServiceById,
  navigateToClassOfServiceDuplicate,
} from './classOfServicesActions';
import { ClassOfService } from './types';

const EMPTY_PLACEHOLDER = '?';

export function ClassOfServicesPage() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const { showSuccess, showError } = useNotifications();
  const { selectedDomain } = useContextStore();
  const dateFormatter = useMemo(() => new Intl.DateTimeFormat(undefined), []);

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [classOfServiceToDelete, setClassOfServiceToDelete] = useState<ClassOfService | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const { fetchData } = useClassOfServiceList({
    domainId: selectedDomain?.id,
  });

  const availableColumns = useMemo(
    () => [
      { key: 'name', label: t('classOfServices.className'), visible: true, sortable: true },
      {
        key: 'featureCodeCos',
        label: t('classOfServices.featureCodeCos'),
        visible: true,
        sortable: false,
      },
      {
        key: 'dialRuleRestriction',
        label: t('classOfServices.dialRuleRestriction'),
        visible: true,
        sortable: false,
      },
      { key: 'isMain', label: t('classOfServices.isMain'), visible: true, sortable: true },
      { key: 'createdAt', label: t('classOfServices.createdAt'), visible: true, sortable: true },
      { key: 'actions', label: t('common.actions'), visible: true, sortable: false },
    ],
    [t]
  );

  const {
    data,
    loading,
    columns,
    pagination,
    searchQuery,
    sortBy,
    sortOrder,
    handleSearch,
    handleSort,
    handlePageChange,
    handleItemsPerPageChange,
    handleColumnToggle,
    refresh,
  } = useDataGrid({
    fetchData,
    initialColumns: availableColumns,
    initialSortBy: 'createdAt',
    initialSortOrder: 'desc',
    initialLimit: 10,
    moduleName: 'class-of-services',
  });

  // Refresh data when domain changes
  useEffect(() => {
    if (selectedDomain?.id !== undefined) {
      refresh();
    }
  }, [selectedDomain?.id]);

  const processedColumns = useMemo(
    () =>
      columns.map((column) => {
        if (column.key === 'name') {
          return {
            ...column,
            render: (_value: unknown, item: ClassOfService) => (
              <span className="font-medium text-foreground">{item.name}</span>
            ),
          };
        }

        if (column.key === 'featureCodeCos') {
          return {
            ...column,
            render: (_value: unknown, item: ClassOfService) => (
              <span className={`text-sm ${item.featureCodeCos?.name ? 'text-muted-foreground' : 'text-green-600 dark:text-green-400'}`}>
                {item.featureCodeCos?.name || t('classOfServices.allAllowed')}
              </span>
            ),
          };
        }

        if (column.key === 'dialRuleRestriction') {
          return {
            ...column,
            render: (_value: unknown, item: ClassOfService) => (
              <span className={`text-sm ${item.dialRuleRestriction?.name ? 'text-muted-foreground' : 'text-green-600 dark:text-green-400'}`}>
                {item.dialRuleRestriction?.name || t('classOfServices.noRestrictions')}
              </span>
            ),
          };
        }

        if (column.key === 'isMain') {
          return {
            ...column,
            render: (_value: unknown, item: ClassOfService) => (
              <span
                className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${item.isMain
                  ? 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200'
                  : 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-200'
                  }`}
              >
                {item.isMain ? t('classOfServices.isMain') : t('common.no')}
              </span>
            ),
          };
        }

        if (column.key === 'createdAt') {
          return {
            ...column,
            render: (_value: unknown, item: ClassOfService) => {
              const date = new Date(item.createdAt);
              return (
                <span className="text-sm text-muted-foreground">
                  {Number.isNaN(date.getTime())
                    ? EMPTY_PLACEHOLDER
                    : dateFormatter.format(date)}
                </span>
              );
            },
          };
        }

        return column;
      }),
    [columns, dateFormatter, t]
  );

  const handleCreate = useCallback(() => {
    navigate('/pbx/class-of-service/class/new');
  }, [navigate]);

  const handleEdit = useCallback((classOfService: ClassOfService) => {
    if (classOfService.id) {
      navigate(`/pbx/class-of-service/class/${classOfService.uuid || classOfService.id}`);
    }
  }, [navigate]);

  const handleDuplicate = useCallback(
    (classOfService: ClassOfService) => {
      navigateToClassOfServiceDuplicate(navigate, classOfService.uuid ?? classOfService.id);
    },
    [navigate]
  );

  const handleDelete = useCallback((classOfService: ClassOfService) => {
    setClassOfServiceToDelete(classOfService);
    setShowDeleteModal(true);
  }, []);

  const confirmDelete = useCallback(async () => {
    if (!classOfServiceToDelete) {
      return;
    }

    setIsDeleting(true);
    try {
      await deleteClassOfServiceById(classOfServiceToDelete.id);

      showSuccess(t('classOfServices.messages.deleteSuccess'));
      setShowDeleteModal(false);
      setClassOfServiceToDelete(null);
      refresh();
    } catch (error) {
      const message =
        error instanceof Error
          ? error.message
          : t('classOfServices.messages.deleteError');
      showError(message);
    } finally {
      setIsDeleting(false);
    }
  }, [classOfServiceToDelete, showError, showSuccess, t, refresh]);

  const renderActions = useCallback(
    (classOfService: ClassOfService) => (
      <div className="flex items-center gap-1.5">
        <SimpleTooltip content={t('classOfServices.tooltips.edit')}>
          <button
            onClick={() => handleEdit(classOfService)}
            className="p-1 rounded transition-colors text-blue-600 hover:text-blue-700 hover:bg-blue-100/60 dark:text-blue-300 dark:hover:text-blue-200 dark:hover:bg-blue-900/30"
          >
            <Edit className="h-4 w-4" />
          </button>
        </SimpleTooltip>
        <SimpleTooltip content={t('classOfServices.tooltips.duplicate')}>
          <button
            onClick={() => handleDuplicate(classOfService)}
            className="p-1 rounded transition-colors text-green-600 hover:text-green-700 hover:bg-green-100/60 dark:text-green-300 dark:hover:text-green-200 dark:hover:bg-green-900/30"
          >
            <Copy className="h-4 w-4" />
          </button>
        </SimpleTooltip>
        {!classOfService.isMain && (
          <SimpleTooltip content={t('classOfServices.tooltips.delete')}>
            <button
              onClick={() => handleDelete(classOfService)}
              className="p-1 rounded transition-colors text-red-600 hover:text-red-700 hover:bg-red-100/60 dark:text-red-300 dark:hover:text-red-200 dark:hover:bg-red-900/30"
            >
              <Trash2 className="h-4 w-4" />
            </button>
          </SimpleTooltip>
        )}
      </div>
    ),
    [handleDelete, handleDuplicate, handleEdit, t]
  );

  return (
    <ModulePageContainer
      currentView="list"
      listView={
        <DataGridSimple
          data={data}
          columns={processedColumns}
          loading={loading}
          pagination={pagination}
          searchQuery={searchQuery}
          sortBy={sortBy}
          sortOrder={sortOrder}
          onSearch={handleSearch}
          onSort={handleSort}
          onPageChange={handlePageChange}
          onItemsPerPageChange={handleItemsPerPageChange}
          onColumnToggle={handleColumnToggle}
          onAdd={handleCreate}
          onEdit={handleEdit}
          onDuplicate={handleDuplicate}
          onDelete={handleDelete}
          title={t('classOfServices.title')}
          titleIcon={<Shield className="h-5 w-5 text-primary" />}
          addButtonText={t('classOfServices.actions.add')}
          searchPlaceholder={t('common.search')}
          renderActions={renderActions}
        />
      }
      formView={null}
    >
      <ConfirmationModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={confirmDelete}
        title={t('classOfServices.deleteClassOfService')}
        message={t('classOfServices.messages.confirmDeleteDescription', {
          name: classOfServiceToDelete?.name ?? '',
        })}
        confirmText={t('common.delete')}
        cancelText={t('common.cancel')}
        type="danger"
        isLoading={isDeleting}
      />
    </ModulePageContainer>
  );
}

export default ClassOfServicesPage;
